import { useEffect, useRef, useState } from 'react';

/**
 * Rascunho local dos formularios de cadastro (Credenciado, Condutor, Monitor,
 * Veiculo) — so CREATE, nunca edicao: quem chama passa `habilitado=false` fora
 * desse caso.
 *
 * Guarda em IndexedDB (fonte de longo prazo) com espelho em localStorage (leitura
 * SINCRONA no mount, para `carregarRascunho`). Sem anexo, so campo serializavel.
 *
 * RASCUNHO TEM PRAZO ({@link VALIDADE_DO_RASCUNHO_MS}). Um formulario de cadastro
 * pode ter CPF ou dado de terceiro digitado e abandonado antes do "Salvar" — sem
 * expiracao, isso ficava indefinidamente no navegador de quem preencheu. Toda
 * leitura confere a idade do registro e apaga o que passou do prazo, dos dois
 * lugares (localStorage e IndexedDB), antes de devolver qualquer coisa a quem
 * chamou.
 */

const ATRASO_DE_GRAVACAO_MS = 1500;
const NOME_DO_BANCO = 'teg-autosave';
const ARMAZEM = 'rascunhos';
const VERSAO_DO_BANCO = 1;
/** Sete dias. Alem disso, o rascunho e tratado como abandonado. */
const VALIDADE_DO_RASCUNHO_MS = 7 * 24 * 60 * 60 * 1000;

interface RegistroDeRascunho<T> {
  dados: T;
  gravadoEm: number;
}

function chaveDoLocalStorage(chave: string): string {
  return `rascunho:${chave}`;
}

function abrirBanco(): Promise<IDBDatabase | null> {
  return new Promise((resolver) => {
    try {
      if (typeof indexedDB === 'undefined') {
        resolver(null);
        return;
      }
      const pedido = indexedDB.open(NOME_DO_BANCO, VERSAO_DO_BANCO);
      pedido.onupgradeneeded = () => {
        const banco = pedido.result;
        if (!banco.objectStoreNames.contains(ARMAZEM)) {
          banco.createObjectStore(ARMAZEM);
        }
      };
      pedido.onsuccess = () => resolver(pedido.result);
      pedido.onerror = () => resolver(null);
    } catch {
      resolver(null);
    }
  });
}

async function gravarNoBanco(chave: string, valor: unknown): Promise<void> {
  const banco = await abrirBanco();
  if (!banco) {
    return;
  }
  await new Promise<void>((resolver) => {
    try {
      const transacao = banco.transaction(ARMAZEM, 'readwrite');
      transacao.objectStore(ARMAZEM).put(valor, chave);
      transacao.oncomplete = () => resolver();
      transacao.onerror = () => resolver();
      transacao.onabort = () => resolver();
    } catch {
      resolver();
    }
  });
  banco.close();
}

async function apagarDoBanco(chave: string): Promise<void> {
  const banco = await abrirBanco();
  if (!banco) {
    return;
  }
  await new Promise<void>((resolver) => {
    try {
      const transacao = banco.transaction(ARMAZEM, 'readwrite');
      transacao.objectStore(ARMAZEM).delete(chave);
      transacao.oncomplete = () => resolver();
      transacao.onerror = () => resolver();
      transacao.onabort = () => resolver();
    } catch {
      resolver();
    }
  });
  banco.close();
}

function lerRegistro<T>(chave: string): RegistroDeRascunho<T> | null {
  try {
    const bruto = localStorage.getItem(chaveDoLocalStorage(chave));
    if (!bruto) {
      return null;
    }
    const registro = JSON.parse(bruto) as RegistroDeRascunho<T>;
    if (Date.now() - registro.gravadoEm > VALIDADE_DO_RASCUNHO_MS) {
      limparRascunho(chave);
      return null;
    }
    return registro;
  } catch {
    return null;
  }
}

/** Hook principal. Grava `dados` (com atraso) quando `habilitado` for true. */
export function useRascunhoDeFormulario<T>(
  chave: string,
  dados: T,
  habilitado: boolean,
): Date | null {
  const referenciaDoTemporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [gravadoEm, setGravadoEm] = useState<Date | null>(() => {
    const registro = lerRegistro<T>(chave);
    return registro?.gravadoEm ? new Date(registro.gravadoEm) : null;
  });

  useEffect(() => {
    if (!habilitado) {
      return undefined;
    }

    if (referenciaDoTemporizador.current) {
      clearTimeout(referenciaDoTemporizador.current);
    }
    referenciaDoTemporizador.current = setTimeout(() => {
      const registro: RegistroDeRascunho<T> = { dados, gravadoEm: Date.now() };
      try {
        localStorage.setItem(chaveDoLocalStorage(chave), JSON.stringify(registro));
      } catch {
        // Local storage cheio — segue so com o IndexedDB.
      }
      void gravarNoBanco(chave, registro);
      setGravadoEm(new Date(registro.gravadoEm));
    }, ATRASO_DE_GRAVACAO_MS);

    return () => {
      if (referenciaDoTemporizador.current) {
        clearTimeout(referenciaDoTemporizador.current);
      }
    };
  }, [chave, dados, habilitado]);

  return gravadoEm;
}

/** Leitura sincrona do rascunho, a partir do espelho em localStorage. */
export function carregarRascunho<T>(chave: string): T | null {
  const registro = lerRegistro<T>(chave);
  return registro?.dados ?? null;
}

/** Apaga o rascunho do localStorage e do IndexedDB. */
export function limparRascunho(chave: string): void {
  try {
    localStorage.removeItem(chaveDoLocalStorage(chave));
  } catch {
    // Sem local storage: segue so limpando o IndexedDB.
  }
  void apagarDoBanco(chave);
}
